import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ClaimsRoiAlignmentViewModel } from '../../domain/reportContext/claimsRoiAlignment';

type Props = {
  alignment: ClaimsRoiAlignmentViewModel | null;
  title?: string;
  compact?: boolean;
  testId?: string;
};

function renderList(items: string[], limit: number): string {
  const trimmed = items.filter(Boolean).slice(0, limit);
  return trimmed.length > 0 ? trimmed.join(', ') : 'Not recorded';
}

const ClaimsRoiAlignmentCard: React.FC<Props> = ({
  alignment,
  title = 'Claims / ROI Alignment',
  compact = false,
  testId = 'claims-roi-alignment-card',
}) => {
  if (!alignment) return null;

  const industryHints = alignment.industrySectorHints.length > 0
    ? alignment.industrySectorHints
    : alignment.targetIndustryHints;

  const detailRows = [
    { label: 'Claims alignment category', value: alignment.claimsAlignmentCategory || 'Not recorded' },
    { label: 'Cost-driver family', value: alignment.claimsCostDriverFamily || 'Not recorded' },
    { label: 'Commercial priority', value: alignment.commercialPriority || 'Not recorded' },
    {
      label: 'Priority basis',
      value: renderList(alignment.commercialPriorityBasis, compact ? 3 : 4),
    },
    {
      label: 'Claim characteristics',
      value: renderList(alignment.claimCharacteristicHints, compact ? 2 : 3),
    },
    {
      label: 'Target industries',
      value: renderList(industryHints, compact ? 3 : 4),
    },
    {
      label: 'Benefit-type lens',
      value: renderList(alignment.benefitTypeRelevance, compact ? 2 : 3),
    },
    {
      label: 'Recommended pilot templates',
      value: renderList(alignment.recommendedPilotTemplates, compact ? 2 : 3),
    },
    {
      label: 'Recommended first scan',
      value: alignment.recommendedFirstScan || 'Not recorded',
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{ p: compact ? 1.5 : 2, borderRadius: 3 }}
      data-testid={testId}
    >
      <Stack spacing={compact ? 1 : 1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Tooltip title={alignment.claimBoundary || 'Claim boundary not recorded'}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
          <Chip size="small" variant="outlined" label={alignment.sourceLabel || 'Curated mapping'} />
          {alignment.commercialPriority ? (
            <Chip
              size="small"
              color={alignment.commercialPriority === 'future' ? 'default' : 'primary'}
              variant="outlined"
              label={`Priority: ${alignment.commercialPriority}`}
            />
          ) : null}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {alignment.uxSummary || alignment.reportSummary || 'Claim-safe commercial alignment available.'}
        </Typography>
        {alignment.roiRationale ? (
          <Typography variant="body2" color="text.secondary">
            {alignment.roiRationale}
          </Typography>
        ) : null}
        {alignment.buyerPainLanguage ? (
          <Typography variant="body2" color="text.secondary">
            {alignment.buyerPainLanguage}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: '1fr', md: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {detailRows.map((row) => (
            <Box key={row.label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

export default ClaimsRoiAlignmentCard;
